import type { ILog } from '@monofe/util';
import { BroadcastChannel } from 'broadcast-channel';
import type { ICallbackBuilder } from '../interface/callback';
import type {
    IChannel,
    IChannelController,
    IChannelEventMap,
    IChannelID,
} from '../interface/channel';
import { IChannelStatus } from '../interface/channel';

function createChannel(callbackBuilder: ICallbackBuilder, id: IChannelID, logger: ILog): IChannel {
    const callbackRegistry = callbackBuilder.build<IChannelEventMap>();

    const handler = new BroadcastChannel(id, {
        type: 'idb',
    });

    handler.onmessage = (message: unknown): void => {
        const callbacks = callbackRegistry.eventListeners('NewMessage');
        for (const callback of callbacks) {
            callback(message);
        }
    };

    const channel = {
        id: (): string => id,
        status: (): IChannelStatus =>
            handler.isClosed ? IChannelStatus.Expired : IChannelStatus.Active,
        postMessage: (message: unknown): void => {
            handler.postMessage(message).catch(error => {
                logger.error(`Failed to post message, error: ${JSON.stringify(error)}`);
            });
        },
        addEventListener: <
            Event extends keyof IChannelEventMap,
            Callback extends IChannelEventMap[Event],
        >(
            key: string,
            event: Event,
            callback: Callback,
        ): void => {
            callbackRegistry.subscribe(callback, event, key);
        },
        removeListener: <Event extends keyof IChannelEventMap>(key: string, event: Event): void => {
            const callbacks = callbackRegistry.search(key, event)!;
            for (const callback of callbacks) {
                callbackRegistry.unsubscribe(callback, event);
            }
        },
        terminate: (): void => {
            handler
                .close()
                .then(() => {
                    const callbacks = callbackRegistry.eventListeners('StatusChanged');
                    for (const callback of callbacks) {
                        callback(IChannelStatus.Expired);
                    }
                })
                .catch(error => {
                    logger.error(`Failed to terminate connection: ${JSON.stringify(error)}`);
                });
        },
    };

    return channel;
}

function channel(logger: ILog, callbackBuilder: ICallbackBuilder): IChannelController {
    const records: Map<IChannelID, IChannel> = new Map();

    const controller = {
        channel: (key: IChannelID): IChannel => {
            const channel = records.get(key) ?? createChannel(callbackBuilder, key, logger);
            records.set(key, channel);
            return channel;
        },
        all: (): IChannel[] => Array.from(records.values()),
    };

    return controller;
}

export default channel;
